'use client';

import { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  TextField,
  Stack,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  InputAdornment,
  Switch,
  Tooltip,
  IconButton
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import FormDialog from '../../../components/FormDialog';
import PickerField from '../../../components/PickerField';
import { useNotifier } from '../../../components/Notifier';
import { useLanguage } from '../../../utils/i18n';
import usePickerPagination from '../../../hooks/usePickerPagination';

import { useCompanyDataList } from '../../../stores/companyStore';
import { useMasterFuelPriceList } from '../../../stores/masterFuelPriceStore';
import { useMasterVehicleList } from '../../../stores/masterVehicleStore';
import {
  getFuelPriceHistoryById,
  createFuelPriceHistory,
  createFuelPriceHistoryBatch,
  updateFuelPriceHistory
} from '../../../stores/fuelPriceHistoryStore';
import MasterCompanyAddEdit from '../company/MasterCompanyAddEdit';
import MasterFuelPriceAddEdit from './MasterFuelPriceAddEdit';
import MasterVehicleAddEdit from '../vehicle/MasterVehicleAddEdit';
import { formatRupiahInput, parseRupiahInput } from '../../../utils/numberFormatter';

const FuelPriceHistoryAddEdit = ({ open, id, onClose, onSuccess }) => {
  const { t } = useLanguage();
  const notify = useNotifier();
  const [loading, setLoading] = useState(false);
  const [isBatch, setIsBatch] = useState(false);

  const [initialValues, setInitialValues] = useState({
    company: '',
    fuel_price: '',
    fuel_price_desc: '',
    fuel_type: '',
    fuel_type_name: '',
    fuel_type_code: '',
    fuel_type_category: '',
    fuel_type_desc: '',
    fuel_type_uom: '',
    price: '',
    description: '',
    valid_at: dayjs(),
    vehicle: '',
    license_plate: '',
    vehicle_name: '',
    vehicle_type: '',
    vehicle_type_name: '',
    vehicle_ids: [],
    is_update_vehicle: false,
    master_fuel_price_val: '',
    master_fuel_price_valid_at: ''
  });

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });
  const companyOptions = companyPickerParams.options;

  useEffect(() => {
    if (!open) return;

    let mounted = true;
    const fetchInitial = async () => {
      setLoading(true);
      try {
        if (id) {
          setIsBatch(false);
          const data = await getFuelPriceHistoryById(id);
          if (!mounted) return;
          if (data) {
            const masterPriceVal = data?.master_fuel_price_join?.price ?? data?.fuel_price_val ?? data?.price ?? '';
            const masterPriceValidAt = data?.master_fuel_price_join?.valid_at ?? data?.fuel_price_valid_at ?? data?.valid_at ?? '';
            const autoIsUpdate =
              (data.is_update_vehicle ?? data.isUpdateVehicle) != null
                ? Boolean(data.is_update_vehicle ?? data.isUpdateVehicle)
                : Boolean(
                    masterPriceVal !== '' &&
                      Number(data.price) === Number(masterPriceVal) &&
                      data.valid_at &&
                      masterPriceValidAt &&
                      dayjs(data.valid_at).format('YYYY-MM-DD') === dayjs(masterPriceValidAt).format('YYYY-MM-DD')
                  );

            setInitialValues({
              company: data.company ?? data.company_id ?? '',
              fuel_price: data.fuel_price ?? data.fuel_price_id ?? data.price_fuel_id ?? '',
              fuel_price_desc: data.fuel_price_desc ?? data.description ?? '',
              fuel_type: data.fuel_type ?? data.fuel_type_id ?? '',
              fuel_type_name: data.fuel_type_name ?? data?.fuel_type_join?.name ?? data?.join?.fuel_type_name ?? '',
              fuel_type_code: data.fuel_type_code ?? data?.fuel_type_join?.code ?? data?.join?.fuel_type_code ?? '',
              fuel_type_category: data.fuel_type_category ?? data?.fuel_type_join?.category ?? data?.join?.fuel_type_category ?? '',
              fuel_type_desc: data.fuel_type_desc ?? data?.fuel_type_join?.description ?? data?.join?.fuel_type_desc ?? '',
              fuel_type_uom: data.fuel_type_uom ?? data?.fuel_type_join?.uom_type ?? data?.join?.fuel_type_uom ?? '',
              price: data.price ?? '',
              description: data.description ?? '',
              valid_at: data.valid_at ? dayjs(data.valid_at) : dayjs(),
              vehicle: data.vehicle ?? data.vehicle_id ?? '',
              license_plate: data.license_plate ?? data?.vehicle_join?.license_plate ?? data?.join?.license_plate ?? '',
              vehicle_name: data.vehicle_name ?? data?.vehicle_join?.name ?? data?.join?.vehicle_name ?? '',
              vehicle_type: data.vehicle_type ?? data.vehicle_type_id ?? '',
              vehicle_type_name: data.vehicle_type_name ?? data?.vehicle_join?.vehicle_type_name ?? data?.join?.vehicle_type_name ?? '',
              vehicle_ids: Array.isArray(data.vehicle_ids)
                ? data.vehicle_ids
                : data.vehicle
                  ? [data.vehicle]
                  : data.vehicle_id
                    ? [data.vehicle_id]
                    : [],
              is_update_vehicle: autoIsUpdate,
              master_fuel_price_val: masterPriceVal,
              master_fuel_price_valid_at: masterPriceValidAt
            });
          }
        } else {
          let defaultCompany = '';
          const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
          if (stored && stored !== 'all') {
            defaultCompany = String(stored);
          }
          setInitialValues({
            company: defaultCompany,
            fuel_price: '',
            fuel_price_desc: '',
            fuel_type: '',
            fuel_type_name: '',
            fuel_type_code: '',
            fuel_type_category: '',
            fuel_type_desc: '',
            fuel_type_uom: '',
            price: '',
            description: '',
            valid_at: dayjs(),
            vehicle: '',
            license_plate: '',
            vehicle_name: '',
            vehicle_type: '',
            vehicle_type_name: '',
            vehicle_ids: [],
            is_update_vehicle: false,
            master_fuel_price_val: '',
            master_fuel_price_valid_at: ''
          });
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
        notify({ message: t('notify.failedLoadData', 'Failed to load data'), severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchInitial();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, open]);

  const validationSchema = Yup.object().shape({
    company: Yup.mixed().required(t('validation.companyRequired', 'Company is required')),
    fuel_price: Yup.mixed().required('Master Fuel Price is required'),
    valid_at: Yup.date()
      .required(t('validation.dateRequired', 'Valid At date is required'))
      .test('is-not-after-fuel-price', function (value) {
        const { master_fuel_price_valid_at } = this.parent;
        if (!value || !master_fuel_price_valid_at) return true;
        const maxDate = dayjs(master_fuel_price_valid_at).endOf('day');
        if (dayjs(value).isAfter(maxDate)) {
          return this.createError({
            message: `Tanggal tidak boleh lebih dari Valid At Fuel Price (${dayjs(master_fuel_price_valid_at).format('DD/MM/YYYY')})`
          });
        }
        return true;
      }),
    vehicle: Yup.mixed().when('isBatch', {
      is: (val) => val === false,
      then: (schema) => schema.required('Vehicle is required'),
      otherwise: (schema) => schema.notRequired()
    }),
    vehicle_ids: Yup.array().when('isBatch', {
      is: (val) => val === true,
      then: (schema) => schema.min(1, 'At least one vehicle is required'),
      otherwise: (schema) => schema.notRequired()
    })
  });

  const handleClose = () => {
    formik.resetForm();
    setIsBatch(false);
    if (onClose) onClose();
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    context: { isBatch }, // pass context for conditional validation if needed
    onSubmit: async (values, { setSubmitting }) => {
      try {
        let response;
        if (isBatch) {
          const payload = {
            company: Number(values.company),
            fuel_price: Number(values.fuel_price),
            fuel_type: Number(values.fuel_type),
            price: Number(values.price),
            description: values.description ?? '',
            valid_at: dayjs(values.valid_at).format('YYYY-MM-DDT00:00:00Z'),
            vehicle_ids: values.vehicle_ids.map((vid) => Number(vid))
          };
          response = await createFuelPriceHistoryBatch(payload, values.is_update_vehicle);
          notify({ message: 'Batch Created successfully', severity: 'success' });
        } else {
          const payload = {
            company: Number(values.company),
            fuel_price: Number(values.fuel_price),
            fuel_type: Number(values.fuel_type),
            price: Number(values.price),
            description: values.description ?? '',
            valid_at: dayjs(values.valid_at).format('YYYY-MM-DDT00:00:00Z'),
            vehicle: Number(values.vehicle),
            fuel_type_name: values.fuel_type_name,
            fuel_type_code: values.fuel_type_code,
            fuel_type_category: values.fuel_type_category,
            fuel_type_desc: values.fuel_type_desc,
            fuel_type_uom: values.fuel_type_uom
          };
          if (id) {
            response = await updateFuelPriceHistory(id, payload, values.is_update_vehicle);
            notify({ message: t('notify.update', 'Updated successfully'), severity: 'success' });
          } else {
            response = await createFuelPriceHistory(payload, values.is_update_vehicle);
            notify({ message: t('notify.create', 'Created successfully'), severity: 'success' });
          }
        }

        if (response?.code === 400 || response?.status === false || response?.error) {
          throw response;
        }

        handleClose();
        if (onSuccess) onSuccess();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.data?.message || err?.message || 'Gagal menyimpan data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const clearVehicleState = () => {
    formik.setFieldValue('vehicle', '');
    formik.setFieldValue('license_plate', '');
    formik.setFieldValue('vehicle_name', '');
    formik.setFieldValue('vehicle_type', '');
    formik.setFieldValue('vehicle_type_name', '');
    formik.setFieldValue('vehicle_ids', []);
  };

  const handleModeChange = (newIsBatch) => {
    setIsBatch(newIsBatch);
    clearVehicleState();
  };

  const uniqueByValue = (arr = []) => {
    const map = new Map();
    (arr || []).forEach((item) => {
      const key = String(item.value ?? item.id ?? '');
      if (key && !map.has(key)) map.set(key, item);
    });
    return Array.from(map.values());
  };

  const fuelPricePickerParams = usePickerPagination({
    fetchHook: useMasterFuelPriceList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue, formik.values.company],
    companyId: formik.values.company,
    searchKey: 'all',
    formatOption: (item) => {
      const fuelName = item?.fuel_type_join?.name || item.fuel_type_name || item.name || '';
      const fuelCode = item?.fuel_type_join?.code || item.fuel_type_code || item.code || '';
      const fuelTypeId =
        item?.fuel_type_join?.id || item.fuel_type_id || (typeof item.fuel_type === 'object' ? item.fuel_type?.id : item.fuel_type) || '';
      const formattedPrice = item.price != null && item.price !== '' ? 'Rp ' + Number(item.price).toLocaleString('id-ID') : '-';

      let fuelTypeLabel = fuelName;
      if (fuelCode) fuelTypeLabel += ` - ${fuelCode}`;
      if (fuelTypeId) fuelTypeLabel += ` - FUEL TYPE ID: ${fuelTypeId}`;

      return {
        value: item.id,
        label: fuelTypeLabel ? `${fuelTypeLabel} - ${formattedPrice}` : formattedPrice,
        original: item
      };
    }
  });

  const selectedFuelPriceOption = formik.values.fuel_price
    ? [
        {
          value: formik.values.fuel_price,
          label: (() => {
            const fuelName = formik.values.fuel_type_name || '';
            const fuelCode = formik.values.fuel_type_code || '';
            const fuelTypeId = formik.values.fuel_type || '';
            const formattedPrice =
              formik.values.price != null && formik.values.price !== '' ? 'Rp ' + Number(formik.values.price).toLocaleString('id-ID') : '';
            let fuelTypeLabel = fuelName;
            if (fuelCode) fuelTypeLabel += ` - ${fuelCode}`;
            if (fuelTypeId) fuelTypeLabel += ` - ${fuelTypeId}`;
            return fuelTypeLabel
              ? `${fuelTypeLabel}${formattedPrice ? ` - ${formattedPrice}` : ''}`
              : `Fuel Price ${formik.values.fuel_price}`;
          })(),
          original: {
            id: formik.values.fuel_price,
            price: formik.values.price,
            fuel_type_name: formik.values.fuel_type_name,
            fuel_type_code: formik.values.fuel_type_code,
            fuel_type_category: formik.values.fuel_type_category,
            fuel_type_desc: formik.values.fuel_type_desc,
            fuel_type_uom: formik.values.fuel_type_uom
          }
        }
      ]
    : [];

  const fuelPriceOptions = uniqueByValue([...selectedFuelPriceOption, ...fuelPricePickerParams.options]);

  const vehiclePickerParams = usePickerPagination({
    fetchHook: useMasterVehicleList,
    buildArgs: (state) => {
      const companies = formik.values.company ? [{ value: formik.values.company }] : [];
      return [state.page, state.pageSize, companies, state.searchKey, state.searchValue];
    },
    searchKey: 'license_plate',
    formatOption: (item) => ({
      value: item.id,
      label: item.license_plate && item.license_plate === item.name ? item.license_plate : `${item.license_plate} - ${item.name || ''}`,
      original: item
    })
  });

  const selectedVehicleOption = formik.values.vehicle
    ? [
        {
          value: formik.values.vehicle,
          label:
            formik.values.license_plate && formik.values.license_plate === formik.values.vehicle_name
              ? formik.values.license_plate
              : formik.values.license_plate
                ? `${formik.values.license_plate} - ${formik.values.vehicle_name || ''}`
                : formik.values.vehicle_name || `Vehicle ${formik.values.vehicle}`,
          original: {
            id: formik.values.vehicle,
            license_plate: formik.values.license_plate,
            name: formik.values.vehicle_name,
            vehicle_type_name: formik.values.vehicle_type_name
          }
        }
      ]
    : [];

  const vehicleOptions = uniqueByValue([...selectedVehicleOption, ...vehiclePickerParams.options]);

  useEffect(() => {
    if (formik.values.master_fuel_price_val !== '' && formik.values.master_fuel_price_valid_at) {
      const isSamePrice = Number(formik.values.price) === Number(formik.values.master_fuel_price_val);
      const historyDateStr = formik.values.valid_at ? dayjs(formik.values.valid_at).format('YYYY-MM-DD') : '';
      const masterDateStr = formik.values.master_fuel_price_valid_at
        ? dayjs(formik.values.master_fuel_price_valid_at).format('YYYY-MM-DD')
        : '';
      const isSameDate = Boolean(historyDateStr && masterDateStr && historyDateStr === masterDateStr);

      const shouldUpdate = isSamePrice && isSameDate;
      if (formik.values.is_update_vehicle !== shouldUpdate) {
        formik.setFieldValue('is_update_vehicle', shouldUpdate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.price, formik.values.valid_at, formik.values.master_fuel_price_val, formik.values.master_fuel_price_valid_at]);

  const isSameDateAsMaster = Boolean(
    formik.values.master_fuel_price_valid_at &&
      formik.values.valid_at &&
      dayjs(formik.values.valid_at).format('YYYY-MM-DD') === dayjs(formik.values.master_fuel_price_valid_at).format('YYYY-MM-DD')
  );

  const dialogActions = (
    <>
      <Button onClick={handleClose} disabled={formik.isSubmitting}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={formik.handleSubmit}
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{ textTransform: 'none', borderRadius: 1, px: 3, fontWeight: 700 }}
      >
        {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
      </Button>
    </>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormDialog
        open={open}
        onClose={handleClose}
        title={id ? t('general.edit', 'Edit') + ' Fuel Price History' : t('general.add', 'Add') + ' Fuel Price History'}
        maxWidth="md"
        fullHeight={false}
        actions={dialogActions}
      >
        <Box sx={{ width: '100%' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <form onSubmit={formik.handleSubmit}>
              {!id && (
                <Box sx={{ mb: 3 }}>
                  <RadioGroup row value={isBatch ? 'batch' : 'single'} onChange={(e) => handleModeChange(e.target.value === 'batch')}>
                    <FormControlLabel value="single" control={<Radio />} label="Single Vehicle" />
                    <FormControlLabel value="batch" control={<Radio />} label="Batch (Multiple Vehicles)" />
                  </RadioGroup>
                </Box>
              )}

              <Grid container spacing={3}>
                <Grid item size={{ xs: 12, md: 6 }}>
                  <Stack spacing={2}>
                    <PickerField
                      label={t('general.company', 'Company') + ' *'}
                      value={formik.values.company}
                      {...companyPickerParams}
                      options={companyOptions}
                      onChange={(opt) => {
                        const val = typeof opt === 'object' ? opt?.value : opt;
                        formik.setFieldValue('company', val ?? null);
                        clearVehicleState();

                        // Clear fuel price selection & details
                        formik.setFieldValue('fuel_price', '');
                        formik.setFieldValue('fuel_price_desc', '');
                        formik.setFieldValue('price', '');
                        formik.setFieldValue('fuel_type', '');
                        formik.setFieldValue('fuel_type_name', '');
                        formik.setFieldValue('fuel_type_code', '');
                        formik.setFieldValue('fuel_type_category', '');
                        formik.setFieldValue('fuel_type_desc', '');
                        formik.setFieldValue('fuel_type_uom', '');
                        formik.setFieldValue('master_fuel_price_val', '');
                        formik.setFieldValue('master_fuel_price_valid_at', '');
                      }}
                      error={formik.touched.company && !!formik.errors.company}
                      helperText={formik.touched.company && formik.errors.company}
                      allowAdd
                      AddComponent={MasterCompanyAddEdit}
                    />

                    <PickerField
                      label={'Master Fuel Price *'}
                      value={formik.values.fuel_price}
                      {...fuelPricePickerParams}
                      options={fuelPriceOptions}
                      onChange={(opt) => {
                        const val = typeof opt === 'object' ? opt?.value : opt;
                        formik.setFieldValue('fuel_price', val ?? null);

                        const selectedObj =
                          typeof opt === 'object' ? opt?.original : fuelPriceOptions.find((o) => String(o.value) === String(val))?.original;
                        if (selectedObj && val != null && val !== '') {
                          formik.setFieldValue('price', selectedObj.price || '');
                          formik.setFieldValue('valid_at', selectedObj.valid_at ? dayjs(selectedObj.valid_at) : dayjs());
                          formik.setFieldValue('fuel_price_desc', selectedObj.description || '');

                          formik.setFieldValue('fuel_type', selectedObj.fuel_type || selectedObj.fuel_type_id || '');
                          formik.setFieldValue('fuel_type_name', selectedObj.fuel_type_name || '');
                          formik.setFieldValue('fuel_type_code', selectedObj.fuel_type_code || '');
                          formik.setFieldValue('fuel_type_category', selectedObj.fuel_type_category || '');
                          formik.setFieldValue('fuel_type_desc', selectedObj.fuel_type_desc || '');
                          formik.setFieldValue('fuel_type_uom', selectedObj.fuel_type_uom || '');

                          formik.setFieldValue('master_fuel_price_val', selectedObj.price || '');
                          formik.setFieldValue('master_fuel_price_valid_at', selectedObj.valid_at || '');
                        } else {
                          // Clear fuel price and type info fields when selection is cleared
                          formik.setFieldValue('price', '');
                          formik.setFieldValue('valid_at', dayjs());
                          formik.setFieldValue('fuel_price_desc', '');
                          formik.setFieldValue('fuel_type', '');
                          formik.setFieldValue('fuel_type_name', '');
                          formik.setFieldValue('fuel_type_code', '');
                          formik.setFieldValue('fuel_type_category', '');
                          formik.setFieldValue('fuel_type_desc', '');
                          formik.setFieldValue('fuel_type_uom', '');
                          formik.setFieldValue('master_fuel_price_val', '');
                          formik.setFieldValue('master_fuel_price_valid_at', '');
                        }
                      }}
                      disabled={!formik.values.company}
                      error={formik.touched.fuel_price && !!formik.errors.fuel_price}
                      helperText={
                        !formik.values.company
                          ? t('general.selectCompanyFirst', '* Please select a company first')
                          : formik.touched.fuel_price && formik.errors.fuel_price
                      }
                      allowAdd
                      AddComponent={MasterFuelPriceAddEdit}
                    />
                    <DatePicker
                      label="Valid At *"
                      value={formik.values.valid_at}
                      onChange={(newValue) => {
                        formik.setFieldValue('valid_at', newValue);
                        if (
                          newValue &&
                          formik.values.master_fuel_price_valid_at &&
                          dayjs(newValue).format('YYYY-MM-DD') === dayjs(formik.values.master_fuel_price_valid_at).format('YYYY-MM-DD')
                        ) {
                          if (formik.values.master_fuel_price_val !== '') {
                            formik.setFieldValue('price', formik.values.master_fuel_price_val);
                          }
                        }
                      }}
                      maxDate={formik.values.master_fuel_price_valid_at ? dayjs(formik.values.master_fuel_price_valid_at) : undefined}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'medium',
                          error: formik.touched.valid_at && !!formik.errors.valid_at,
                          helperText:
                            (formik.touched.valid_at && formik.errors.valid_at) ||
                            (formik.values.master_fuel_price_valid_at
                              ? `Max ${dayjs(formik.values.master_fuel_price_valid_at).format('DD/MM/YYYY')}`
                              : '')
                        }
                      }}
                    />

                    <TextField
                      fullWidth
                      size="medium"
                      id="price"
                      name="price"
                      label="Price (IDR) *"
                      type="text"
                      disabled={isSameDateAsMaster && Boolean(formik.values.master_fuel_price_valid_at)}
                      value={formatRupiahInput(formik.values.price)}
                      onChange={(e) => {
                        const parsed = parseRupiahInput(e.target.value);
                        formik.setFieldValue('price', parsed);
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.touched.price && !!formik.errors.price}
                      helperText={
                        (formik.touched.price && formik.errors.price) ||
                        (isSameDateAsMaster && formik.values.master_fuel_price_valid_at
                          ? t('fuelPriceHistory.changeValidAtToEditPrice', 'Change Valid At first to edit the nominal price')
                          : '')
                      }
                      InputProps={{
                        startAdornment: <InputAdornment position="start">Rp</InputAdornment>
                      }}
                    />

                    <TextField
                      fullWidth
                      size="medium"
                      id="description"
                      name="description"
                      label={t('general.description', 'Description')}
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.description && !!formik.errors.description}
                      helperText={formik.touched.description && formik.errors.description}
                      multiline
                      rows={2}
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(formik.values.is_update_vehicle)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              formik.setFieldValue('is_update_vehicle', checked);
                              if (checked) {
                                if (formik.values.master_fuel_price_val !== '') {
                                  formik.setFieldValue('price', formik.values.master_fuel_price_val);
                                }
                                if (formik.values.master_fuel_price_valid_at) {
                                  formik.setFieldValue('valid_at', dayjs(formik.values.master_fuel_price_valid_at));
                                }
                              }
                            }}
                            name="is_update_vehicle"
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {t('fuelPriceHistory.isVehicleUpdate', 'Is Vehicle Update')}
                          </Typography>
                        }
                      />
                      <Tooltip
                        title={t(
                          'fuelPriceHistory.isVehicleUpdateTooltip',
                          'Determines whether this price history will update the fuel price data in the Vehicle Master. It is automatically set to Active (True) if the Price and Valid At values ​​exactly match those in the Master Fuel Price.'
                        )}
                        arrow
                      >
                        <IconButton size="small" sx={{ color: 'text.secondary', p: 0.5 }}>
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Stack>
                </Grid>

                <Grid item size={{ xs: 12, md: 6 }}>
                  <Stack spacing={2}>
                    {!isBatch ? (
                      <>
                        <PickerField
                          label={'Vehicle *'}
                          value={formik.values.vehicle}
                          {...vehiclePickerParams}
                          options={vehicleOptions}
                          onChange={(opt) => {
                            const val = typeof opt === 'object' ? opt?.value : opt;
                            formik.setFieldValue('vehicle', val ?? null);

                            const selectedObj =
                              typeof opt === 'object'
                                ? opt?.original
                                : vehicleOptions.find((o) => String(o.value) === String(val))?.original;
                            if (selectedObj && val != null && val !== '') {
                              formik.setFieldValue('license_plate', selectedObj.license_plate || '');
                              formik.setFieldValue('vehicle_name', selectedObj.name || '');
                              formik.setFieldValue('vehicle_type', selectedObj.vehicle_type_id || '');
                              formik.setFieldValue(
                                'vehicle_type_name',
                                selectedObj.vehicle_type_name ||
                                  selectedObj.vehicle_type_join?.name ||
                                  selectedObj.join?.vehicle_type_name ||
                                  ''
                              );
                            } else {
                              formik.setFieldValue('license_plate', '');
                              formik.setFieldValue('vehicle_name', '');
                              formik.setFieldValue('vehicle_type', '');
                              formik.setFieldValue('vehicle_type_name', '');
                            }
                          }}
                          disabled={!formik.values.company}
                          error={formik.touched.vehicle && !!formik.errors.vehicle}
                          helperText={
                            !formik.values.company
                              ? t('general.selectCompanyFirst', '* Please select a company first')
                              : formik.touched.vehicle && formik.errors.vehicle
                          }
                          allowAdd
                          AddComponent={MasterVehicleAddEdit}
                        />
                        <TextField fullWidth size="small" label="License Plate" value={formik.values.license_plate} disabled />
                        <TextField fullWidth size="small" label="Vehicle Name" value={formik.values.vehicle_name} disabled />
                        <TextField fullWidth size="small" label="Vehicle Type" value={formik.values.vehicle_type_name} disabled />
                      </>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2">Select Vehicles *</Typography>
                        </Box>
                        <PickerField
                          label={'Vehicles'}
                          multiSelect
                          value={formik.values.vehicle_ids}
                          {...vehiclePickerParams}
                          options={vehicleOptions}
                          onChange={(val) => {
                            formik.setFieldValue('vehicle_ids', Array.isArray(val) ? val : []);
                          }}
                          disabled={!formik.values.company}
                          error={formik.touched.vehicle_ids && !!formik.errors.vehicle_ids}
                          helperText={
                            !formik.values.company
                              ? t('general.selectCompanyFirst', '* Please select a company first')
                              : formik.touched.vehicle_ids && formik.errors.vehicle_ids
                          }
                          allowAdd
                          AddComponent={MasterVehicleAddEdit}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formik.values.vehicle_ids.length} vehicle(s) selected
                        </Typography>
                      </>
                    )}

                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Fuel Type Info
                      </Typography>
                      <Typography variant="body2">
                        <strong>Name:</strong> {formik.values.fuel_type_name || '-'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Code:</strong> {formik.values.fuel_type_code || '-'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Category:</strong> {formik.values.fuel_type_category || '-'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>UOM:</strong> {formik.values.fuel_type_uom || '-'}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </form>
          )}
        </Box>
      </FormDialog>
    </LocalizationProvider>
  );
};

export default FuelPriceHistoryAddEdit;
